import invariant from "tiny-invariant"
import { type BigintIsh, Fraction, type Rounding } from "../math"
import { Amount } from "./Amount"
import type { TokenType } from "./types"

/**
 * The `Price` class represents a price quote for a trading pair, typically between two different currencies.
 * It extends the `Fraction` class and provides methods for handling price-related calculations and formatting.
 */
export class Price<
  TBase extends TokenType,
  TQuote extends TokenType,
> extends Fraction {
  /**
   * A scalar factor used to adjust the raw fraction with respect to the decimals of the base and quote currencies.
   * @type {Fraction}
   */
  readonly scalar: Fraction // used to adjust the raw fraction w/r/t the decimals of the {base,quote}Token

  /**
   * Constructs a `Price` instance with the specified base and quote currencies, denominator, and numerator.
   *
   * @param {TokenType} baseCurrency - The base currency (denominator).
   * @param {TokenType} quoteCurrency - The quote currency (numerator).
   * @param {BigintIsh} denominator - The denominator of the price fraction.
   * @param {BigintIsh} numerator - The numerator of the price fraction.
   */
  constructor(
    readonly baseCurrency: TBase, // input i.e. denominator
    readonly quoteCurrency: TQuote, // output i.e. numerator
    denominator: BigintIsh,
    numerator: BigintIsh,
  ) {
    super(numerator, denominator)

    this.baseCurrency = baseCurrency
    this.quoteCurrency = quoteCurrency

    // Calculate the scalar factor based on currency decimals.
    this.scalar = new Fraction(
      10n ** BigInt(baseCurrency.decimals),
      10n ** BigInt(quoteCurrency.decimals),
    )
  }

  /**
   * Flip the price, switching the base and quote currencies.
   *
   * @returns {Price<TokenType, TokenType>} - A new `Price` instance with inverted base and quote currencies.
   */
  override invert(): Price<TQuote, TBase> {
    return new Price(
      this.quoteCurrency,
      this.baseCurrency,
      this.numerator,
      this.denominator,
    )
  }

  /**
   * Multiply the price by another price, returning a new price.
   * The other price must have the same base currency as this price's quote currency.
   *
   * @param {Price<TokenType, TokenType>} other - The other price to multiply with.
   * @returns {Price<TokenType, TokenType>} - A new `Price` instance representing the result of the multiplication.
   */
  override multiply<TOtherQuote extends TokenType>(
    other: Price<TQuote, TOtherQuote>,
  ): Price<TBase, TOtherQuote> {
    invariant(this.quoteCurrency.equals(other.baseCurrency), "TOKEN")
    const fraction = super.multiply(other)
    return new Price(
      this.baseCurrency,
      other.quoteCurrency,
      fraction.denominator,
      fraction.numerator,
    )
  }

  /**
   * Return the amount of quote currency corresponding to a given amount of the base currency.
   *
   * @param {Amount<TokenType>} currencyAmount - The amount of base currency to quote against the price.
   * @returns {Amount<TokenType>} - An `Amount` instance representing the quoted amount in the quote currency.
   */
  quote(currencyAmount: Amount<TBase>): Amount<TQuote> {
    invariant(currencyAmount.currency.equals(this.baseCurrency), "TOKEN")
    const result = super.multiply(currencyAmount)
    return Amount.fromFractionalAmount(
      this.quoteCurrency,
      result.numerator,
      result.denominator,
    )
  }

  /**
   * Get the value scaled by decimals for formatting.
   *
   * @private
   * @returns {Fraction} - A `Fraction` instance adjusted for decimals.
   */
  private get adjustedForDecimals(): Fraction {
    return super.multiply(this.scalar)
  }

  /**
   * Converts the `Price` into a string representation with a specified number of significant digits.
   *
   * @param {number} significantDigits - The number of significant digits to display.
   * @param {object} format - Optional formatting options for the string representation.
   * @param {Rounding} rounding - The rounding mode to use.
   * @returns {string} - A string representation of the `Price`.
   * @override
   */
  override toSignificant(
    significantDigits = 6,
    format?: object,
    rounding?: Rounding,
  ): string {
    return this.adjustedForDecimals.toSignificant(
      significantDigits,
      format,
      rounding,
    )
  }

  /**
   * Converts the `Price` into a string representation with a fixed number of decimal places.
   *
   * @param {number} decimalPlaces - The number of decimal places to display.
   * @param {object} format - Optional formatting options for the string representation.
   * @param {Rounding} rounding - The rounding mode to use.
   * @returns {string} - A string representation of the `Price`.
   * @override
   */
  override toFixed(
    decimalPlaces = 4,
    format?: object,
    rounding?: Rounding,
  ): string {
    return this.adjustedForDecimals.toFixed(decimalPlaces, format, rounding)
  }
}
